import { useSearchStore } from '../../stores/search-store.js';
import { useSearch } from '../../hooks/useSearch.js';
import { useBarClause } from '../../hooks/useBarClause.js';
import { CardNameInput } from './CardNameInput.js';
import { OracleTextInput } from './OracleTextInput.js';
import { ColorsFilter } from './ColorsFilter.js';
import { StatsFilters } from './StatsFilters.js';
import { TypeLineMultiSelect } from './TypeLineMultiSelect.js';
import { SetInput } from './SetInput.js';
import { ManaCostBuilder } from './ManaCostBuilder.js';

const RARITY_OPTIONS = [
  { value: '', label: 'Any Rarity' },
  { value: 'common', label: 'Common' },
  { value: 'uncommon', label: 'Uncommon' },
  { value: 'rare', label: 'Rare' },
  { value: 'mythic', label: 'Mythic' },
];

const selectClass =
  'bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-gray-300 focus:outline-none focus:border-orange-500 transition-colors cursor-pointer';

export function AdvancedSearchPanel() {
  const isOpen = useSearchStore((s) => s.isAdvancedOpen);
  const rarity = useSearchStore((s) => s.advancedFilters.rarity);
  const setAdvancedFilter = useSearchStore((s) => s.setAdvancedFilter);
  const setAdvancedOpen = useSearchStore((s) => s.setAdvancedOpen);
  const clearAdvancedFilters = useSearchStore((s) => s.clearAdvancedFilters);
  const { execute } = useSearch();
  const { writeClause } = useBarClause();

  const ALL_ADVANCED_PREFIXES = [
    'name:', 'o:', 't:', 'c=', 'c>=', 'c<=', 'm:',
    'pow>', 'pow=', 'pow<', 'tou>', 'tou=', 'tou<',
    'mv>', 'mv=', 'mv<', 'e:', 'r:',
  ];

  const handleClearAll = () => {
    clearAdvancedFilters();
    // Remove all prefix-based clauses
    for (const prefix of ALL_ADVANCED_PREFIXES) {
      writeClause(prefix, null);
    }
    // Also remove grouped set clauses like (e:khm OR e:dmu)
    const bar = useSearchStore.getState().userInput;
    const cleaned = bar.replace(/\(e:\S+(?:\s+OR\s+e:\S+)*\)/g, '').replace(/\s{2,}/g, ' ').trim();
    useSearchStore.getState().setUserInput(cleaned);
  };

  const handleRarityChange = (value: string) => {
    setAdvancedFilter('rarity', value);
    writeClause('r:', value || null);
  };

  if (!isOpen) return null;

  return (
    <div className="border border-gray-800 rounded-lg p-3 space-y-3 bg-gray-900/50">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 font-medium">Advanced Filters</span>
        <button
          onClick={handleClearAll}
          className="text-[10px] text-gray-600 hover:text-gray-400 transition-colors"
        >
          Clear All
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CardNameInput />
        <OracleTextInput />
      </div>

      <TypeLineMultiSelect />
      <ColorsFilter />
      <ManaCostBuilder />
      <StatsFilters />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <SetInput />
        <div className="space-y-1">
          <label className="text-[10px] text-gray-500 uppercase tracking-wider">Rarity</label>
          <select
            value={rarity}
            onChange={(e) => handleRarityChange(e.target.value)}
            className={selectClass + ' w-full'}
          >
            {RARITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <button
        onClick={() => { setAdvancedOpen(false); void execute(1); }}
        className="w-full py-1.5 text-xs font-medium rounded-lg bg-orange-600 hover:bg-orange-500 text-white transition-colors"
      >
        Search
      </button>
    </div>
  );
}
